import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from "../../assets/logo-aft4.png";
import { priceFormatterV3, toEuro } from '../../settings/themeSettings';

// ─── Palette ────────────────────────────────────────────────────────────────
const PRIMARY  = '#000000';
const LIGHT_BG = '#f5f5f5';
const BORDER   = '#000000';
const WHITE    = '#ffffff';
const GRAY_TXT = '#000000';

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: WHITE,
        paddingTop: 20,
        paddingBottom: 36,
        paddingHorizontal: 22,
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#000000',
    },

    // ── En-tête fixe ──
    pageHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        paddingBottom: 6,
        marginBottom: 10,
    },
    logo: { width: 70, height: 28 },
    docTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: '#000000',
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    refBox: {
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#000000',
        borderRadius: 3,
        paddingHorizontal: 6,
        paddingVertical: 2,
        fontSize: 8,
        color: '#000000',
        fontFamily: 'Helvetica-Bold',
    },

    // ── Pied de page fixe ──
    pageFooter: {
        position: 'absolute',
        bottom: 14,
        left: 22,
        right: 22,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 0.5,
        borderTopColor: '#000000',
        paddingTop: 4,
        fontSize: 7,
        color: '#000000',
    },

    // ── Tableau ──
    table: {
        width: '100%',
        borderWidth: 0.5,
        borderColor: '#000000',
        borderStyle: 'solid',
    },
    // En-tête de tableau (répété sur chaque page)
    tableHead: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderBottomWidth: 0.5,
        borderBottomColor: '#000000',
    },
    thFrais:  { width: '31%', padding: 4, fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#000000', borderRightWidth: 0.5, borderRightColor: '#000000' },
    thValue:  { width: '23%', padding: 4, fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#000000' },
    thLast:   { width: '23%', padding: 4, fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#000000',
        minHeight: 18,
    },
    tableRowAlt: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#000000',
        backgroundColor: '#ffffff',
        minHeight: 18,
    },
    tableRowEmpty: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#000000',
        minHeight: 16,
    },
    tdFrais: { width: '31%', padding: 4, fontSize: 7.5, borderRightWidth: 0.5, borderRightColor: '#000000', color: '#000000' },
    tdValue: { width: '23%', padding: 4, fontSize: 7.5, textAlign: 'right', borderRightWidth: 0.5, borderRightColor: '#000000', color: '#000000' },
    tdLast:  { width: '23%', padding: 4, fontSize: 7.5, textAlign: 'right', color: '#000000' },

    // ── Ligne total ──
    totalRow: {
        flexDirection: 'row',
        backgroundColor: '#e0e0e0',
        borderTopWidth: 1,
        borderTopColor: '#000000',
        minHeight: 22,
        alignItems: 'center',
    },
    totalLabel: { width: '31%', padding: 4, fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#000000', borderRightWidth: 0.5, borderRightColor: '#000000' },
    totalValue: { width: '23%', padding: 4, fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'right', borderRightWidth: 0.5, borderRightColor: '#000000' },
    totalLast:  { width: '23%', padding: 4, fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'right' },

    // ── Solde ──
    soldeBox: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 8,
    },
    soldeLabel: {
        borderWidth: 0.5,
        borderColor: '#000000',
        backgroundColor: '#f5f5f5',
        color: '#000000',
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderTopLeftRadius: 3,
        borderBottomLeftRadius: 3,
    },
    soldeValue: {
        borderWidth: 0.5,
        borderLeftWidth: 0,
        borderColor: '#000000',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderTopRightRadius: 3,
        borderBottomRightRadius: 3,
        minWidth: 80,
        textAlign: 'right',
    },
});

// ─── Composant ───────────────────────────────────────────────────────────────
const EnveloppePDF = ({ dossier, euro = false }) => {
    // Helpers locaux
    const fmt = (v) => {
        if (!v || Number(v) === 0) return euro ? '0 €' : '0';
        return euro ? toEuro(Number(v)) : priceFormatterV3(Number(v));
    };
    const fmtDual = (v) => {
        if (!v || Number(v) === 0) return euro ? '0 €' : '0';
        return euro ? toEuro(Number(v)) : priceFormatterV3(Number(v));
    };
    const MIN_ROWS = 14;

    const enveloppes  = dossier.envelops || [];

    const totalEstime = enveloppes.reduce((s, e) => s + Number(e.montant       || 0), 0);
    const totalReel   = enveloppes.reduce((s, e) => s + Number(e.depouillement || 0), 0);
    const totalEcart  = totalEstime - totalReel;

    const emptyRows = Math.max(0, MIN_ROWS - enveloppes.length);

    return (
        <Document>
            <Page size="A4" style={S.page}>

                {/* ── En-tête fixe ── */}
                <View style={S.pageHeader} fixed>
                    <Image style={S.logo} source={logo} />
                    <Text style={S.docTitle}>Enveloppe Dossier</Text>
                    <Text style={S.refBox}>{dossier.reference || ''}</Text>
                </View>

                {/* ── Pied de page fixe ── */}
                <View style={S.pageFooter} fixed render={({ pageNumber, totalPages }) => (
                    <>
                        <Text>Africa For Tourism — Enveloppe {dossier.reference}</Text>
                        <Text>Page {pageNumber} / {totalPages}</Text>
                    </>
                )} />

                {/* ── Tableau ── */}
                <View style={S.table}>

                    {/* En-tête colonnes — répété sur chaque page */}
                    <View style={S.tableHead} fixed>
                        <Text style={S.thFrais}>Frais</Text>
                        <Text style={S.thValue}>Montant estimé</Text>
                        <Text style={S.thValue}>Montant réel</Text>
                        <Text style={S.thLast}>Écart</Text>
                    </View>

                    {/* Lignes de données */}
                    {enveloppes.map((env, i) => {
                        const mEstime = Number(env.montant       || 0);
                        const mReel   = Number(env.depouillement || 0);
                        const ecart   = mEstime - mReel;
                        return (
                            <View key={i} style={i % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                                <Text style={S.tdFrais}>{env.frais || ''}</Text>
                                <Text style={S.tdValue}>{fmt(mEstime)}</Text>
                                <Text style={S.tdValue}>{fmt(mReel)}</Text>
                                <Text style={S.tdLast}>
                                    {fmt(ecart)}
                                </Text>
                            </View>
                        );
                    })}

                    {/* Lignes vides pour saisie manuelle */}
                    {Array.from({ length: emptyRows }).map((_, i) => (
                        <View key={`e-${i}`} style={S.tableRowEmpty}>
                            <Text style={S.tdFrais}> </Text>
                            <Text style={S.tdValue}> </Text>
                            <Text style={S.tdValue}> </Text>
                            <Text style={S.tdLast}> </Text>
                        </View>
                    ))}

                    {/* Ligne totaux */}
                    <View style={S.totalRow}>
                        <Text style={S.totalLabel}>TOTAL</Text>
                        <Text style={S.totalValue}>{fmtDual(totalEstime)}</Text>
                        <Text style={S.totalValue}>{fmtDual(totalReel)}</Text>
                        <Text style={S.totalLast}>
                            {fmtDual(totalEcart)}
                        </Text>
                    </View>
                </View>

                {/* ── Solde enveloppe ── */}
                <View style={S.soldeBox}>
                    <Text style={S.soldeLabel}>Solde Enveloppe</Text>
                    <Text style={S.soldeValue}>{fmtDual(totalEcart)}</Text>
                </View>

            </Page>
        </Document>
    );
};

export default EnveloppePDF;
